import type { GetChildCount, IServerSideGetRowsRequest } from "ag-grid-enterprise";
import type { GridApi, ICellRendererParams } from "~/components/grid/AgGrid";
import type { EventJobScope } from "~produce/production/schedule/events/helpers/eventJobScope";
import type { EventWeekScope } from "~produce/production/schedule/events/helpers/eventWeekScope";
import type { GeneratePoSourceRow } from "~produce/purchasing/orders/create/helpers/generatePoPrefill";

import { type GridRequestInput } from "~/gql/graphqlBase.generated";
import unwrapMultiFilterModel from "~/helpers/ag-grid/unwrapMultiFilterModel";
import materialRequirementBasisText from "~produce/helpers/materialRequirementBasisText";
import { withEventJobScope } from "~produce/production/schedule/events/helpers/eventJobScope";
import { withEventWeekScope } from "~produce/production/schedule/events/helpers/eventWeekScope";
import { LOT_CODE_QUERY_PARAM } from "~produce/production/schedule/helpers/scheduleSsrmRequestTranslation";
import { GENERATE_PO_PARAM } from "~produce/purchasing/orders/create/helpers/generatePoPrefill";

export const EVENT_SCHEDULE_GRID_ID = "eventSchedule";

export type EventScheduleTab = "material" | "space" | "task";

export type EventScheduleType = "MATERIAL" | "SPACE" | "TASK";

export type EventScheduleRow = {
	available: number | null;
	categoryCode: string | null;
	description: string;
	duration: number | null;
	eventDetailId: string;
	eventId: string;
	eventName: string;
	eventType: EventScheduleType;
	jobCode: string;
	jobNumber: number;
	locationId: string | null;
	locationName: string | null;
	lotCode: string | null;
	lotNumber: number | null;
	materialItemDescription: string | null;
	materialItemId: string | null;
	multiple: number | null;
	onHand: number | null;
	openPoQuantity: number | null;
	poNumber: string | null;
	poStatus: string | null;
	productionCategoryCode: string | null;
	productionJobId: string;
	productionLotId: string;
	productionPlanName: string | null;
	productionSpaceCategoryId: string | null;
	productionSpaceTypeId: string | null;
	projectedOnHand: number | null;
	quantity: number;
	requiredQuantity: number | null;
	rowId: string;
	scheduledIsoWeek: number | null;
	scheduledIsoYear: number | null;
	scheduledWeek: string | null;
	sequence: number | null;
	siteLocationId?: string | null;
	status: string | null;
	taskEventTypeId: string | null;
	taskEventTypeName: string | null;
	triggerDate: string | null;
	triggerId: string | null;
	triggerName: string;
	typeCode: string | null;
	unitLabel: string | null;
	weekReadyIsoWeek: number | null;
	weekStartIsoWeek: number | null;
};

const EVENT_TYPE_COL = "eventType";

const EVENT_TYPE_BY_TAB: Record<EventScheduleTab, EventScheduleType> = {
	material: "MATERIAL",
	space: "SPACE",
	task: "TASK"
};

export const eventTypeForTab = (tab: EventScheduleTab): EventScheduleType => EVENT_TYPE_BY_TAB[tab];

const GRID_DATA_KEY_BY_TAB: Record<EventScheduleTab, string> = {
	material: "event-schedule-material-v4",
	space: "event-schedule-space-v3",
	task: "event-schedule-task-v3"
};

export const eventScheduleGridDataKey = (tab: EventScheduleTab): string => GRID_DATA_KEY_BY_TAB[tab];

export const eventScheduleRowId = (row: EventScheduleRow): string => row.rowId;

const GROUP_ROW_ID_PREFIX = "group";

export interface EventScheduleRowIdParams {
	groupFields: readonly (string | undefined)[];
	level: number;
	parentKeys: readonly string[] | undefined;
	row: EventScheduleRow | undefined;
}

export const buildEventScheduleRowId = ({ groupFields, level, parentKeys, row }: EventScheduleRowIdParams): string => {
	if (level >= groupFields.length && row?.rowId) return row.rowId;
	const groupField = groupFields[level];
	const groupValue = groupField ? (row as Record<string, unknown> | undefined)?.[groupField] : undefined;
	return JSON.stringify([GROUP_ROW_ID_PREFIX, ...(parentKeys ?? []), String(groupValue ?? "")]);
};

export const parseGroupRowId = (id: string): string[] | null => {
	try {
		const parsed: unknown = JSON.parse(id);
		if (!Array.isArray(parsed) || parsed[0] !== GROUP_ROW_ID_PREFIX) return null;
		return parsed.slice(1).map(String);
	} catch {
		return null;
	}
};

export const blankGroupInnerRendererSelector =
	(blankLabel: string) =>
	(params: Pick<ICellRendererParams, "node" | "value">): { component: () => string } | undefined =>
		params.node.group && params.value === "" ? { component: () => blankLabel } : undefined;

export const getEventScheduleChildCount: GetChildCount = (data) => data?.childCount;

export const selectedGroupKeyPaths = (toggledNodeIds: readonly string[]): string[][] =>
	toggledNodeIds.map(parseGroupRowId).filter((keys): keys is string[] => keys !== null);

export const groupFieldsOf = (api: Pick<GridApi, "getRowGroupColumns">): string[] =>
	api.getRowGroupColumns().map((column) => column.getColDef().field ?? column.getColId());

export const dropRowsInGroups = (
	rows: EventScheduleRow[],
	groupKeyPaths: readonly string[][],
	groupFields: readonly string[]
): EventScheduleRow[] =>
	groupKeyPaths.length === 0
		? rows
		: rows.filter(
				(row) =>
					!groupKeyPaths.some((keys) =>
						keys.every((key, level) => String((row as Record<string, unknown>)[groupFields[level]] ?? "") === key)
					)
			);

export const buildGroupLeafRequest = (
	groupKeys: readonly string[],
	groupFields: readonly string[],
	filterModel: IServerSideGetRowsRequest["filterModel"]
): IServerSideGetRowsRequest => ({
	endRow: 0,
	filterModel,
	groupKeys: [...groupKeys],
	pivotCols: [],
	pivotMode: false,
	rowGroupCols: groupFields.slice(0, groupKeys.length).map((field) => ({ displayName: field, field, id: field })),
	sortModel: [],
	startRow: 0,
	valueCols: []
});

export const buildEventScheduleRowRequest = ({
	eventDetailId,
	productionJobId
}: Pick<EventScheduleRow, "eventDetailId" | "productionJobId">): IServerSideGetRowsRequest =>
	buildGroupLeafRequest([], [], {
		eventDetailId: { filterType: "set", values: [eventDetailId] },
		productionJobId: { filterType: "set", values: [productionJobId] }
	});

export type FetchGroupLeafRows = (groupKeys: string[]) => Promise<EventScheduleRow[]>;

export const resolveGeneratePoRows = async (
	selectedRows: readonly EventScheduleRow[],
	groupKeyPaths: readonly string[][],
	fetchGroupLeafRows: FetchGroupLeafRows
): Promise<EventScheduleRow[]> => {
	const groupLeafRows: EventScheduleRow[] = [];
	for (const groupKeys of groupKeyPaths) {
		groupLeafRows.push(...(await fetchGroupLeafRows(groupKeys)));
	}
	const rowsById = new Map<string, EventScheduleRow>();
	for (const row of [...selectedRows, ...groupLeafRows]) {
		rowsById.set(row.rowId, row);
	}
	return [...rowsById.values()];
};

export const rowClickOpensPanel = (event: { data?: unknown; node: { group?: boolean } }): boolean =>
	!event.node.group && event.data != null;

export const isAvailableNegative = (available: number | null): boolean => available != null && available < 0;

export const eventRemovalLabel = (row: EventScheduleRow): string =>
	row.lotCode ? `${row.lotCode} — ${row.eventName}` : row.eventName;

export const withEventTypeScope = (filterModel: unknown, tab: EventScheduleTab): unknown => ({
	...((filterModel as Record<string, unknown> | null) ?? {}),
	[EVENT_TYPE_COL]: { filterType: "set", values: [eventTypeForTab(tab)] }
});

export const eventScheduleExportPrefix = (tab: EventScheduleTab): string => `event-schedule-${tab}`;

export const PRODUCTION_SCHEDULE_PATH = "/produce/production/schedule";

export const productionScheduleLotUrl = (lotCode: string): string =>
	`${PRODUCTION_SCHEDULE_PATH}?${LOT_CODE_QUERY_PARAM}=${encodeURIComponent(lotCode)}`;

export const HARDGOODS_INVENTORY_PATH = "/produce/inventory/hardgoods";

export const PO_CREATE_ROUTE = "/produce/purchasing/orders/new";

export const generatePoCreateUrl = (requestId: string): string =>
	`${PO_CREATE_ROUTE}?${GENERATE_PO_PARAM}=${encodeURIComponent(requestId)}`;

export const eventScheduleRowToGeneratePoSource = (row: EventScheduleRow): GeneratePoSourceRow => ({
	available: row.available,
	eventDetailId: row.eventDetailId,
	itemName: row.materialItemDescription ?? row.eventName,
	jobNumber: row.jobNumber,
	jobQuantity: row.quantity,
	materialItemId: row.materialItemId ?? "",
	multiple: row.multiple,
	productionJobId: row.productionJobId,
	requiredQuantity: row.requiredQuantity,
	siteLocationId: row.siteLocationId ?? null,
	triggerDate: row.triggerDate,
	unitLabel: row.unitLabel
});

const NUMBER_FILTER_COL_IDS: ReadonlySet<string> = new Set(["scheduledIsoWeek", "scheduledIsoYear"]);

export const toServerFilterModel = (filterModel: unknown): unknown =>
	unwrapMultiFilterModel(filterModel, NUMBER_FILTER_COL_IDS);

export const eventRequiredBasisText = (row: EventScheduleRow | undefined, isGroupRow: boolean): string | undefined => {
	if (!row || isGroupRow || row.multiple === null || row.requiredQuantity === null) return undefined;
	return materialRequirementBasisText({
		containers: row.quantity,
		multiple: row.multiple,
		required: row.requiredQuantity,
		unitLabel: row.unitLabel
	});
};

const buildEventScheduleScopeTransform =
	(tab: EventScheduleTab, weekScope: EventWeekScope | null, jobScope: EventJobScope | null = null) =>
	(request: GridRequestInput): GridRequestInput => ({
		...request,
		filterModel: withEventJobScope(
			withEventWeekScope(withEventTypeScope(toServerFilterModel(request.filterModel), tab), weekScope),
			jobScope
		)
	});

export default buildEventScheduleScopeTransform;
